import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Activity, TrendingUp, Wallet, DollarSign, Users } from 'lucide-react';
import { toast } from 'sonner';
import { getAuthenticatedAxios, handleAuthError } from '../utils/api';
import { Label } from './ui/label';

const WarmupDashboard = () => {
  const [analytics, setAnalytics] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedGroup, setSelectedGroup] = useState('');
  const [walletGroups, setWalletGroups] = useState([]);

  useEffect(() => {
    loadWalletGroups();
    fetchData();
    const interval = setInterval(fetchData, 5000); // Обновлять каждые 5 секунд
    return () => clearInterval(interval);
  }, []);

  const loadWalletGroups = async () => {
    try {
      const api = getAuthenticatedAxios();
      const response = await api.get('/api/wallets/groups');
      console.log('Groups API response:', response.data);
      // API returns { count, groups } - no 'success' field
      if (response.data && response.data.groups) {
        setWalletGroups(response.data.groups || []);
        console.log('Loaded groups:', response.data.groups.length);
      } else if (response.data && Array.isArray(response.data)) {
        // If API returns array directly
        setWalletGroups(response.data);
      } else {
        console.warn('Unexpected groups API response format:', response.data);
      }
    } catch (error) {
      console.error('Error loading wallet groups:', error);
      if (!handleAuthError(error, toast)) {
        console.error('Failed to load wallet groups:', error.response?.data || error.message);
      }
    }
  };

  const fetchData = async () => {
    try {
      const api = getAuthenticatedAxios();
      const [analyticsRes, statusRes] = await Promise.all([
        api.get('/api/warmup/analytics').catch(err => {
          handleAuthError(err, toast);
          return { data: { success: false, error: err.message } };
        }),
        api.get('/api/warmup/status').catch(err => {
          handleAuthError(err, toast);
          return { data: { success: false, error: err.message } };
        })
      ]);

      if (analyticsRes.data && analyticsRes.data.success) {
        setAnalytics(analyticsRes.data.analytics);
      } else {
        console.warn('Failed to fetch analytics:', analyticsRes.data?.error || 'Unknown error');
      }
      if (statusRes.data && statusRes.data.success) {
        setStatus(statusRes.data.status);
      } else {
        console.warn('Failed to fetch status:', statusRes.data?.error || 'Unknown error');
      }
    } catch (error) {
      console.error('Error fetching warmup data:', error);
      handleAuthError(error, toast);
    } finally {
      setLoading(false);
    }
  };

  const handleStart = async () => {
    try {
      const api = getAuthenticatedAxios();
      const requestData = selectedGroup ? { wallet_group_name: selectedGroup } : {};
      const response = await api.post('/api/warmup/start', requestData);
      if (response.data.success) {
        const groupText = selectedGroup ? ` с группой "${selectedGroup}"` : ' со всеми кошельками';
        toast.success(`Warmup service started${groupText}`);
        fetchData();
      } else {
        toast.error('Failed to start warmup service');
      }
    } catch (error) {
      if (!handleAuthError(error, toast)) {
        toast.error(`Failed to start warmup service: ${error.response?.data?.detail || error.message}`);
      }
    }
  };

  const handleStop = async () => {
    try {
      const api = getAuthenticatedAxios();
      const response = await api.post('/api/warmup/stop');
      if (response.data.success) {
        toast.success('Warmup service stopped');
        fetchData();
      } else {
        toast.error('Failed to stop warmup service');
      }
    } catch (error) {
      if (!handleAuthError(error, toast)) {
        toast.error(`Failed to stop warmup service: ${error.response?.data?.detail || error.message}`);
      }
    }
  };

  if (loading) {
    return (
      <div className="p-4">
        <div className="text-center py-8">
          <div className="text-lg font-medium">Загрузка данных...</div>
          <div className="text-sm text-gray-500 mt-2">Подключение к серверу</div>
        </div>
      </div>
    );
  }

  // Показываем интерфейс даже если данные не загрузились
  const displayAnalytics = analytics || {
    total_wallets: 0,
    warmed_up_wallets: 0,
    warmed_up_percent: 0,
    total_warmup_operations: 0,
    avg_swaps_per_wallet: 0,
    success_rate: 0,
    today_activity: { swaps_executed: 0, success_rate: 0 },
    active_tokens: 0,
    financial_summary: { total_spent_ton: 0, total_spent_usd: 0 }
  };

  const displayStatus = status || {
    running: false,
    enabled: false
  };

  return (
    <div className="space-y-4">
      {/* Status Card */}
      <Card>
        <CardHeader>
          <CardTitle>Warmup Service Status</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Badge variant={displayStatus.running ? 'default' : 'secondary'}>
                {displayStatus.running ? 'Запущен' : 'Остановлен'}
              </Badge>
              <Badge variant={displayStatus.enabled ? 'default' : 'outline'}>
                {displayStatus.enabled ? 'Включен' : 'Выключен'}
              </Badge>
              {displayStatus.wallet_group_name && (
                <Badge variant="outline" className="flex items-center gap-1">
                  <Users className="h-3 w-3" />
                  Группа: {displayStatus.wallet_group_name}
                </Badge>
              )}
              {!displayStatus.wallet_group_name && displayStatus.running && (
                <Badge variant="outline">Все кошельки</Badge>
              )}
            </div>
            <div className="flex gap-2">
              {displayStatus.running ? (
                <Button onClick={handleStop} variant="destructive">Остановить</Button>
              ) : (
                <Button onClick={handleStart}>Запустить</Button>
              )}
            </div>
          </div>
          
          {!displayStatus.running && (
            <div className="space-y-2">
              <Label htmlFor="wallet-group" className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                Группа кошельков
              </Label>
              <select
                id="wallet-group"
                className="w-full p-2 border border-gray-300 rounded-lg"
                value={selectedGroup}
                onChange={(e) => setSelectedGroup(e.target.value)}
              >
                <option value="">Все кошельки</option>
                {walletGroups.map((group) => (
                  <option key={group.group_name} value={group.group_name}>
                    {group.group_name} ({group.count || group.wallet_count || 0} кошельков)
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500">
                {selectedGroup 
                  ? `Скрипт будет работать только с кошельками из группы "${selectedGroup}"`
                  : 'Скрипт будет работать со всеми кошельками'}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Wallets</CardTitle>
            <Wallet className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{displayAnalytics.total_wallets || 0}</div>
            <p className="text-xs text-muted-foreground">
              {displayAnalytics.warmed_up_wallets || 0} разогрето ({displayAnalytics.warmed_up_percent || 0}%)
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Operations</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{displayAnalytics.total_warmup_operations || 0}</div>
            <p className="text-xs text-muted-foreground">
              Среднее {displayAnalytics.avg_swaps_per_wallet?.toFixed(2) || 0} свапов на кошелек
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Success Rate</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{displayAnalytics.success_rate?.toFixed(2) || 0}%</div>
            <p className="text-xs text-muted-foreground">
              Сегодня: {displayAnalytics.today_activity?.success_rate?.toFixed(2) || 0}%
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Spent</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {displayAnalytics.financial_summary?.total_spent_ton?.toFixed(2) || '0.00'} TON
            </div>
            <p className="text-xs text-muted-foreground">
              ${displayAnalytics.financial_summary?.total_spent_usd?.toFixed(2) || '0.00'} USD
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Today's Activity */}
      <Card>
        <CardHeader>
          <CardTitle>Today's Activity</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <div className="text-sm text-muted-foreground">Свапов выполнено</div>
              <div className="text-2xl font-bold">{displayAnalytics.today_activity?.swaps_executed || 0}</div>
            </div>
            <div>
              <div className="text-sm text-muted-foreground">Успешность</div>
              <div className="text-2xl font-bold">{displayAnalytics.today_activity?.success_rate?.toFixed(2) || 0}%</div>
            </div>
            <div>
              <div className="text-sm text-muted-foreground">Активных токенов</div>
              <div className="text-2xl font-bold">{displayAnalytics.active_tokens || 0}</div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default WarmupDashboard;

